import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";

/**
 * App status data layer (0383): the one `app_status` row, and who last
 * changed it.
 *
 * Read and written straight under RLS, like Settings (0213): everyone may read
 * the row, only the Super Admin may update it, and the 0213 trigger writes the
 * audit row. No Edge Function: there is no cross-table rule here, and the
 * booking server reads the same row to refuse new bookings on its own.
 */

export interface AppStatusRecord {
  maintenance_starts_at: string | null;
  maintenance_back_by: string | null;
  maintenance_message: string | null;
  min_build_at: string | null;
  updated_at: string;
}

const KEY = ["admin-app-status"] as const;

export function useAppStatusRecord() {
  return useQuery({
    queryKey: KEY,
    queryFn: async (): Promise<AppStatusRecord> => {
      const { data, error } = await supabase
        .from("app_status")
        .select(
          "maintenance_starts_at, maintenance_back_by, maintenance_message, min_build_at, updated_at",
        )
        .eq("id", 1)
        .single();
      if (error) throw error;
      return data;
    },
    refetchInterval: 30_000,
  });
}

export type AppStatusPatch = Partial<
  Pick<
    AppStatusRecord,
    "maintenance_starts_at" | "maintenance_back_by" | "maintenance_message" | "min_build_at"
  >
>;

export function useUpdateAppStatus() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (patch: AppStatusPatch) => {
      const { data, error } = await supabase
        .from("app_status")
        .update(patch)
        .eq("id", 1)
        .select("id");
      if (error) throw error;
      // RLS turns a refused update into zero rows, not an error.
      if (!data?.length) throw new Error("Only a Super Admin can change app status.");
    },
    onSettled: () => {
      void client.invalidateQueries({ queryKey: KEY });
      void client.invalidateQueries({ queryKey: ["app-status-last-change"] });
    },
  });
}

export interface LastChange {
  at: string;
  by: string | null;
}

/** The newest audit row for the app_status row, with the changer's name. */
export function useAppStatusLastChange() {
  return useQuery({
    queryKey: ["app-status-last-change"],
    queryFn: async (): Promise<LastChange | null> => {
      const { data, error } = await supabase
        .from("audit_log")
        .select("actor_id, created_at")
        .eq("entity", "app_status:1")
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      if (!data?.created_at) return null;
      let by: string | null = null;
      if (data.actor_id) {
        const staff = await supabase
          .from("staff")
          .select("full_name")
          .eq("id", data.actor_id)
          .maybeSingle();
        by = staff.data?.full_name ?? null;
      }
      return { at: data.created_at, by };
    },
  });
}
